import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { AddQuantityButton } from '../../components/ProductCard/ProductCard';
import MultiSelectDropdown, {
  useMultiSelectDropdown,
} from '../../components/MultiSelectDropdown';
import Button from '../../components/Button';
import { getVariantFromSelectedOptions } from '../utils';

interface Props {
  product: GlobalProduct;
  selectedVariant?: GlobalVariant;
  onSelect: (selectedAddon: { id: string; quantity: number }) => void;
  onConfirm: (selectedOption: MultiSelectOption) => void;
  showQuantityPicker?: boolean;
}

const VariantSliderSelector = ({
  product,
  selectedVariant,
  onSelect,
  onConfirm,
  showQuantityPicker = true,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    options,
    quantity,
    setIsOpen,
    handleOnOptionClicked,
    handleClose,
  } = useVariantSliderSelector(product, selectedVariant, onSelect, onConfirm);

  const isProductAvailable: boolean =
    !!selectedVariant?.available || !selectedVariant?.tracksInventory;

  const hasVariants = product?.variants.length && product.variants.length > 1;

  return (
    <SliderFooter hasVariants={!!hasVariants}>
      {hasVariants ? (
        <MultiSelectDropdown
          isOpen={isOpen}
          options={options}
          onClose={handleClose}
          onOptionClicked={handleOnOptionClicked}
          buttonText={t('addons.save')}
          onSelectClick={() => setIsOpen(!isOpen)}
          selectedOption={selected?.label}
          onConfirm={handleConfirm}
          label={t('addons.type')}
          helperText={
            !selected?.id || !selectedVariant?.available
              ? t('addons.selection_unavailable')
              : ''
          }
          error={!selectedVariant?.available || !selected?.id}
        />
      ) : null}
      <QuantitySelector hasVariants={!!hasVariants}>
        {showQuantityPicker && (
          <AddQuantityButton
            style={{ width: '45%' }}
            onIncrease={increase}
            onDecrease={decrease}
            quantity={quantity}
            available={isProductAvailable}
          />
        )}
        <Button
          style={{
            marginLeft: showQuantityPicker ? '10%' : '',
            width: showQuantityPicker ? '45%' : '100%',
          }}
          onClick={handleSelect}
          filled
          disabled={!selected?.id || !isProductAvailable}
          fullWidth
        >
          {t('product_swap.save')}
        </Button>
      </QuantitySelector>
    </SliderFooter>
  );
};

interface VariantSelectorComponentProps {
  hasVariants: boolean;
}
const SliderFooter = styled.div.attrs({
  className: 'bundle-slider-footer',
})<VariantSelectorComponentProps>`
  width: 100%;
  min-height: ${(props) => (props.hasVariants ? '150px' : '0')};
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f1f2f3;
  background-color: inherit;
`;
const QuantitySelector = styled.div.attrs({
  className: 'bundle-quantity-selector',
})<VariantSelectorComponentProps>`
  width: 100%;
  margin-top: ${(props) => (props.hasVariants ? '20px' : '0')};
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const useVariantSliderSelector = (
  slidePanelAddon: GlobalProduct,
  selectedVariant,
  onSelect: (selectedAddon: { id: string; quantity: number }) => void,
  onConfirm: (selectedOption: MultiSelectOption) => void,
) => {
  const selectedDefaultOption = slidePanelAddon.variantsOptions?.find(
    (variant) => variant.isSelected,
  );
  const initialOption = slidePanelAddon.variantsOptions?.find(
    (variant) => `${variant.id}` === selectedVariant?.id,
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [selected, setSelected] = useState<MultiSelectOption | undefined>(
    initialOption || selectedDefaultOption,
  );
  const { isOpen, setIsOpen, handleOnOptionClicked, options } =
    useMultiSelectDropdown({
      initialOptions: [...(slidePanelAddon?.optionsAndValues || [])],
      isSingleSelect: false,
    });

  const increase = () => setQuantity(quantity + 1);
  const decrease = () => setQuantity(quantity - 1);

  const onSelectVariantOption = () => {
    const selectedObject = options.filter(({ isSelected }) => isSelected);
    const selectedVariant = getVariantFromSelectedOptions(
      selectedObject,
      slidePanelAddon.variants,
    );
    const selectedVariantOption = {
      id: selectedVariant?.id?.toString(),
      label: selectedVariant.name,
    };
    setSelected(selectedVariantOption);
    onConfirm(selectedVariantOption);
  };

  const handleConfirm = () => {
    onSelectVariantOption();
    setIsOpen(false);
  };

  const handleClose = () => {
    handleConfirm();
    setIsOpen(false);
  };

  useDeepCompareEffect(() => {
    onSelectVariantOption();
  }, [options]);

  const handleSelect = () => {
    onSelect({ id: selected?.id as string, quantity });
  };

  return {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    setIsOpen,
    handleOnOptionClicked,
    options,
    quantity,
    handleClose,
  };
};

export default VariantSliderSelector;
